import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Check, ChevronsUpDown, Download, ExternalLink, History, Image as ImageIcon, Paperclip, Search,
  Trash2, Upload,
} from 'lucide-react'
import clsx from 'clsx'
import { Card, EmptyState, Field, Input, Loading, Modal, StatusPill, useToast } from '@/components/ui'
import { useAuth } from '@/lib/auth'
import { useAudit, useMaterials, usePeople } from '@/lib/data'
import { dateTime, ddmmyyyy, qty } from '@/lib/format'
import { needsConversion, conversionText } from '@/lib/domain'
import { enqueue } from '@/lib/sync'
import { supabase } from '@/lib/supabase'
import type { Material } from '@/lib/types'

// ---------------------------------------------------------------------------
// Material picker — searchable, works entirely from the offline cache
// ---------------------------------------------------------------------------
export function MaterialPicker({ value, onChange, exclude, placeholder = 'Search material by name or code…', autoFocus }: {
  value: string | null
  onChange: (m: Material | null) => void
  exclude?: string[]
  placeholder?: string
  autoFocus?: boolean
}) {
  const { data: materials, loading } = useMaterials()
  const [open, setOpen] = useState(false)
  const [term, setTerm] = useState('')
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const onDoc = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false) }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [])

  const selected = materials.find((m) => m.id === value) ?? null

  const results = useMemo(() => {
    const t = term.trim().toLowerCase()
    const pool = materials.filter((m) => !exclude?.includes(m.id))
    if (!t) return pool.slice(0, 60)
    return pool.filter((m) =>
      m.item_name.toLowerCase().includes(t) ||
      m.item_code.toLowerCase().includes(t) ||
      (m.category ?? '').toLowerCase().includes(t) ||
      m.id.toLowerCase().includes(t),
    ).slice(0, 60)
  }, [materials, term, exclude])

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="field flex w-full items-center justify-between gap-2 text-left"
      >
        {/* The NAME alone, on every document (12-09-2026). The picked line used
            to read "06\" Rod Cutting Blade · 6IN-ROD-CUTTING-DISK": the store
            code is ours, not the trade's, and on a requisition or an order it
            only made the line harder to read. The code is still searchable in
            the box below, and still shown against each result there. */}
        <span className={clsx('truncate', !selected && 'text-slate-400')}>
          {selected ? selected.item_name : placeholder}
        </span>
        <ChevronsUpDown className="h-4 w-4 shrink-0 text-slate-400" />
      </button>

      {open && (
        <div className="absolute z-30 mt-1 w-full overflow-hidden rounded-lg border border-slate-200 bg-white shadow-lg">
          <div className="relative border-b border-slate-100 p-2">
            <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              autoFocus={autoFocus !== false}
              className="field pl-8"
              placeholder="Type to search…"
              value={term}
              onChange={(e) => setTerm(e.target.value)}
            />
          </div>
          <div className="max-h-72 overflow-y-auto">
            {loading && <Loading label="Loading materials…" />}
            {!loading && results.length === 0 && (
              <p className="px-3 py-6 text-center text-sm text-slate-500">No material matches “{term}”.</p>
            )}
            {results.map((m) => (
              <button
                key={m.id}
                type="button"
                onClick={() => { onChange(m); setOpen(false); setTerm('') }}
                className="flex w-full items-start gap-2 px-3 py-2 text-left hover:bg-slate-50"
              >
                {m.id === value ? <Check className="mt-0.5 h-4 w-4 text-oval-600" /> : <span className="w-4" />}
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium text-slate-800">{m.item_name}</span>
                  <span className="block truncate text-xs text-slate-500">
                    {m.item_code} · {m.category ?? '—'} · stocked in {m.unit_of_measure}
                    {needsConversion(m) && (
                      <span className="ml-1 font-semibold text-oval-700">
                        · bought in {m.purchase_uom} @ {conversionText(m)}
                      </span>
                    )}
                  </span>
                </span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// UOM conversion helper shown wherever a stock/purchase UOM mismatch exists
// ---------------------------------------------------------------------------
export function ConversionNote({ material, stockQty, purchaseQty }: {
  material: Material | null | undefined
  stockQty?: number
  purchaseQty?: number
}) {
  if (!material || !needsConversion(material)) return null
  return (
    <p className="mt-1 rounded-md bg-oval-50 px-2 py-1 text-xs text-oval-800">
      <strong>Auto-converted.</strong>{' '}
      {stockQty !== undefined && (
        <>{qty(stockQty)} {material.unit_of_measure} = <strong>{qty(stockQty * material.conversion_factor)} {material.purchase_uom}</strong></>
      )}
      {purchaseQty !== undefined && stockQty === undefined && (
        <>{qty(purchaseQty)} {material.purchase_uom} = <strong>{qty(purchaseQty / material.conversion_factor)} {material.unit_of_measure}</strong></>
      )}
      {' '}({conversionText(material)})
    </p>
  )
}

// ---------------------------------------------------------------------------
// Audit trail
// ---------------------------------------------------------------------------
export function AuditTrail({ docType, docId }: { docType: string; docId: string }) {
  const { data, loading } = useAudit(docType, docId)
  // Management 01-09-2026: the trail names the person, not their email address.
  // The email is still STORED on every row - it is the record - but it is no
  // longer drawn. See src/lib/people.ts.
  const people = usePeople()
  return (
    <Card title="Audit trail" subtitle="Every state change, permanently recorded"
          actions={<History className="h-4 w-4 text-slate-400" />}>
      {loading && <Loading />}
      {!loading && data.length === 0 && <p className="text-sm text-slate-500">Nothing recorded yet.</p>}
      <ol className="space-y-3">
        {data.map((a) => (
          <li key={a.id} className="flex gap-3 border-l-2 border-slate-200 pl-3">
            <div className="min-w-0 flex-1">
              <p className="flex flex-wrap items-center gap-1.5 text-sm">
                <span className="font-semibold text-slate-800">{a.action}</span>
                {a.from_status && <span className="text-xs text-slate-400">{a.from_status} →</span>}
                {a.to_status && <StatusPill status={a.to_status} />}
              </p>
              <p className="text-xs text-slate-500">
                {people.name({ id: a.actor_id, email: a.actor_email, role: a.actor_role })}
                {' · '}{dateTime(a.created_at)}
              </p>
              {a.comments && <p className="mt-1 rounded bg-slate-50 px-2 py-1 text-xs text-slate-700">{a.comments}</p>}
            </div>
          </li>
        ))}
      </ol>
    </Card>
  )
}

// ---------------------------------------------------------------------------
// Attachments — queued offline as blobs, uploaded on reconnect
// ---------------------------------------------------------------------------
/** Every kind of file the system knows about, in one place. */
export const ATTACHMENT_KIND_LABEL: Record<string, string> = {
  CHALLAN: 'Delivery challan',
  EWAY: 'E-way bill',
  TEST_CERT: 'Test / calibration certificate',
  PHOTO: 'Photograph',
  INVOICE: 'Vendor invoice',
  QUOTATION: 'Vendor quotation',
  APPROVAL: 'Written approval / email',
  DRAWING: 'Drawing or specification',
  REQUEST: 'Site request slip',
  CREDIT_NOTE: 'Credit note',
  PAYMENT_PROOF: 'Payment proof / bank screenshot',
  // Vendor master papers. Kept in the same vocabulary as everything else so
  // there is one list of document kinds in the system, not two.
  GST_CERT: 'GST registration certificate',
  PAN: 'PAN card',
  BANK: 'Bank account details / cancelled cheque',
  MSME: 'MSME / Udyam certificate',
  AGREEMENT: 'Agreement / work order',
  // Works contracts, 14-09-2026. SIGNED_WO is the one the board actually asked
  // for: the paper the contractor put his name to, scanned back in, so that
  // what OVAL holds on file is the document HE accepted rather than the one it
  // sent. UDYAM is listed separately from MSME because a contractor commonly
  // produces one and not the other, and "MSME / Udyam" as a single kind makes
  // it impossible to tell later which of the two is actually on file.
  SIGNED_WO: 'Signed & accepted work order (scanned)',
  // 0138. Its own kind and not SIGNED_WO, because the two are filed against
  // different documents and an auditor asking "what authorised this extra"
  // must not be handed the order that did not.
  SIGNED_VO: 'Signed & accepted variation order (scanned)',
  // 0140. The contractor's own bill, travelling the other way. Its own kind and
  // not INVOICE, because that one is a materials vendor's invoice against a
  // purchase order and the two are reconciled by different machinery entirely.
  CONTRACTOR_INVOICE: "Contractor's invoice (scanned)",
  UDYAM: 'Udyam Aadhaar',
  EXPERIENCE: 'Experience certificate',
  LABOUR_LICENCE: 'Labour licence / BOCW registration',
  INSURANCE: 'Workmen compensation / insurance policy',
  OTHER: 'Other document',
}

/**
 * What each document sensibly carries. A goods receipt wants the challan; a
 * purchase order wants the quotation it was priced from. Offering all ten
 * everywhere just makes people pick OTHER.
 */
const KIND_SETS: Record<string, string[]> = {
  GRN: ['CHALLAN', 'EWAY', 'TEST_CERT', 'PHOTO', 'INVOICE', 'OTHER'],
  IST: ['CHALLAN', 'EWAY', 'PHOTO', 'OTHER'],
  PR:  ['REQUEST', 'QUOTATION', 'DRAWING', 'PHOTO', 'APPROVAL', 'OTHER'],
  PO:  ['QUOTATION', 'APPROVAL', 'DRAWING', 'OTHER'],
  MIN: ['REQUEST', 'PHOTO', 'APPROVAL', 'OTHER'],
  MRN: ['PHOTO', 'REQUEST', 'OTHER'],
  INVOICE: ['INVOICE', 'OTHER'],
  CREDIT_NOTE: ['CREDIT_NOTE', 'OTHER'],
  PAYMENT: ['PAYMENT_PROOF', 'OTHER'],
  // A payment against a vendor's opening balance (migration 0179). The
  // screenshot Accounts sends is the proof; approval is refused without one.
  OPENING_PAYMENT: ['PAYMENT_PROOF', 'OTHER'],
  // The vendor master. GST certificate first because it is the one Procurement
  // reaches for most, and the one an auditor asks for.
  VENDOR: ['GST_CERT', 'PAN', 'BANK', 'MSME', 'AGREEMENT', 'OTHER'],
  // A works contract. Longer than the others on purpose: a contractor's file is
  // where an audit of a civil job begins, and the papers listed here are the
  // ones WO/OVAL/2026-27/OP003 did not have. QUOTATION is first because it is
  // the one the order itself refers to - that order said "Quotation Ref No:
  // Verbal", which is what happens when there is nowhere to put the paper.
  //
  // SIGNED_WO is offered here too, but the place it is actually asked for is
  // the work order screen after the contractor acknowledges. There is no such
  // thing as a signed copy of an order nobody has sent yet.
  WO: ['QUOTATION', 'SIGNED_WO', 'PAN', 'GST_CERT', 'BANK', 'MSME', 'UDYAM',
       'LABOUR_LICENCE', 'INSURANCE', 'TEST_CERT', 'EXPERIENCE', 'DRAWING',
       'APPROVAL', 'OTHER'],
}
const DEFAULT_KINDS = ['PHOTO', 'OTHER']

export interface PendingAttachment {
  kind: string
  file: File
}

export function AttachmentCollector({ value, onChange, required, hint, docType }: {
  value: PendingAttachment[]
  onChange: (v: PendingAttachment[]) => void
  required?: boolean
  hint?: string
  /** Tailors the list of document kinds on offer. */
  docType?: string
}) {
  const kinds = (docType && KIND_SETS[docType]) || KIND_SETS.GRN || DEFAULT_KINDS
  const [kind, setKind] = useState(kinds[0])

  return (
    <div className="space-y-2">
      <Field
        label="Supporting documents"
        required={required}
        hint={hint ?? 'Challan, e-way bill, test certificates and a photograph of the material. Files are held on this device and upload automatically when you are back online.'}
      >
        <div className="flex flex-wrap gap-2">
          <select className="field w-auto min-w-[190px]" value={kind} onChange={(e) => setKind(e.target.value)}>
            {kinds.map((k) => <option key={k} value={k}>{ATTACHMENT_KIND_LABEL[k] ?? k}</option>)}
          </select>
          <label className="btn-secondary cursor-pointer">
            <Upload className="h-4 w-4" /> Choose file
            <input
              type="file"
              className="hidden"
              accept="image/*,application/pdf"
              capture={kind === 'PHOTO' ? 'environment' : undefined}
              onChange={(e) => {
                const f = e.target.files?.[0]
                if (f) onChange([...value, { kind, file: f }])
                e.target.value = ''
              }}
            />
          </label>
        </div>
      </Field>

      {value.length > 0 && (
        <ul className="space-y-1">
          {value.map((a, i) => (
            <li key={i} className="flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-1.5 text-sm">
              <Paperclip className="h-4 w-4 shrink-0 text-slate-400" />
              <span className="min-w-0 flex-1 truncate">{a.file.name}</span>
              <span className="pill-grey">{ATTACHMENT_KIND_LABEL[a.kind] ?? a.kind}</span>
              <span className="text-xs text-slate-400">{(a.file.size / 1024).toFixed(0)} KB</span>
              <button
                type="button"
                className="rounded p-1 text-slate-400 hover:bg-red-50 hover:text-red-600"
                onClick={() => onChange(value.filter((_, j) => j !== i))}
                aria-label="Remove"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

/**
 * Queue the collected files against a document.
 *
 * `docId` may still be the offline placeholder (`GRN-LOCAL-1754...`). Do not
 * bake it into the storage path here — the sync layer resolves it to the real
 * document number at upload time, so the file lands under the id the rest of
 * the system will look for.
 */
export async function queueAttachments(
  docType: string, docId: string, attachments: PendingAttachment[], bucket = 'doc-attachments',
) {
  for (const a of attachments) {
    const safe = a.file.name.replace(/[^a-zA-Z0-9._-]/g, '_')
    await enqueue({
      kind: 'attachment.upload',
      docType,
      label: `Upload ${a.file.name} for ${docId}`,
      localDocId: docId,
      payload: {
        bucket,
        fileName: `${Date.now()}_${safe}`,
        blob: a.file,
        meta: {
          doc_type: docType, doc_id: docId, kind: a.kind,
          file_name: a.file.name, mime_type: a.file.type || 'application/octet-stream',
        },
      },
    })
  }
}

// ---------------------------------------------------------------------------
// Attachments already on a document — for everyone downstream of the person
// who uploaded them. The Head of Stores approving a goods receipt has to be
// able to open the challan; the Head of Procurement has to see the invoice.
// ---------------------------------------------------------------------------
interface StoredAttachment {
  id: string
  file_name: string
  kind: string
  file_path: string
  mime_type: string | null
  size_bytes: number | null
  uploaded_at: string
}

// ---------------------------------------------------------------------------
// OPENING A FILE, AND SAVING ONE. Two different things.
//
// Every attachment in this system is in a PRIVATE bucket, so nothing can be
// reached by its path. A short-lived signed link is minted on the click and
// nowhere else - it is never rendered into the page, never put in an href the
// browser could keep, and it dies in an hour.
//
// Until 09-09-2026 there was only Open, which hands the file to the browser's
// own viewer. That is right for a quick look at a challan. It is wrong for
// Accounts: she has to FILE the GST certificate against the payment voucher,
// and "Save as" from a PDF viewer saves a signed URL's gibberish, not
// "GST Certificate.pdf".
//
// So the download flag is used. Storage answers with
//   Content-Disposition: attachment; filename="<the name it was uploaded as>"
// and the browser writes the file to disk under its real name, in one click,
// without leaving the screen the person is working on.
//
// Both are on the same policy. There is no way to download a file that could
// not be opened, and no way to open one that could not be downloaded - the
// database decided that before either button was drawn.
// ---------------------------------------------------------------------------

/** A link the browser will DISPLAY. Expires in an hour. */
async function signedViewUrl(bucket: string, path: string): Promise<string> {
  const { data, error } = await supabase.storage.from(bucket).createSignedUrl(path, 3600)
  if (error || !data) throw new Error(error?.message ?? 'no link')
  return data.signedUrl
}

/** A link the browser will SAVE, under `saveAs`. Expires in an hour. */
async function signedDownloadUrl(bucket: string, path: string, saveAs: string): Promise<string> {
  const { data, error } = await supabase.storage
    .from(bucket).createSignedUrl(path, 3600, { download: saveAs })
  if (error || !data) throw new Error(error?.message ?? 'no link')
  return data.signedUrl
}

/**
 * Save a stored file to the person's computer.
 *
 * A hidden anchor rather than window.open. With Content-Disposition set to
 * attachment the browser downloads without navigating, so this leaves no blank
 * tab behind and nothing for a pop-up blocker to swallow - which matters,
 * because Accounts does this several times per voucher.
 */
export async function saveAttachment(
  bucket: string, path: string, fileName: string,
): Promise<void> {
  const url = await signedDownloadUrl(bucket, path, fileName)
  const a = document.createElement('a')
  a.href = url
  a.download = fileName          // ignored cross-origin; the header is what works
  a.rel = 'noopener'
  document.body.appendChild(a)
  a.click()
  a.remove()
}

export function AttachmentList({ docType, docId, title = 'Attached documents', subtitle, bucket = 'doc-attachments' }: {
  docType: string
  docId: string
  title?: string
  subtitle?: string
  bucket?: string
}) {
  const [files, setFiles] = useState<StoredAttachment[]>([])
  const [loading, setLoading] = useState(true)
  const [opening, setOpening] = useState<string | null>(null)
  const [saving, setSaving] = useState<string | null>(null)
  const toast = useToast()

  useEffect(() => {
    let alive = true
    void (async () => {
      setLoading(true)
      const { data } = await supabase
        .from('document_attachments')
        .select('id, file_name, kind, file_path, mime_type, size_bytes, uploaded_at')
        .eq('doc_type', docType).eq('doc_id', docId)
        .order('uploaded_at')
      if (alive) { setFiles((data ?? []) as StoredAttachment[]); setLoading(false) }
    })()
    return () => { alive = false }
  }, [docType, docId])

  async function open(f: StoredAttachment) {
    setOpening(f.id)
    try {
      window.open(await signedViewUrl(bucket, f.file_path), '_blank', 'noopener')
    } catch {
      toast.error('That file could not be opened. It may still be waiting to upload from the site.')
    } finally { setOpening(null) }
  }

  async function save(f: StoredAttachment) {
    setSaving(f.id)
    try {
      await saveAttachment(bucket, f.file_path, f.file_name)
    } catch {
      toast.error('That file could not be downloaded. It may still be waiting to upload from the site.')
    } finally { setSaving(null) }
  }

  return (
    <Card title={title} subtitle={subtitle ?? 'Challan, e-way bill, test certificates and site photographs'}>
      {loading && <Loading label="Loading attachments…" />}

      {!loading && files.length === 0 && (
        <p className="text-sm text-slate-500">
          No documents attached yet — they may still be queued for upload from the site.
        </p>
      )}

      {!loading && files.length > 0 && (
        <ul className="space-y-1">
          {files.map((f) => {
            const image = (f.mime_type ?? '').startsWith('image/')
            return (
              <li key={f.id}
                  className="flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm">
                {image
                  ? <ImageIcon className="h-4 w-4 shrink-0 text-slate-400" />
                  : <Paperclip className="h-4 w-4 shrink-0 text-slate-400" />}
                <button type="button" onClick={() => void open(f)}
                        className="min-w-0 flex-1 truncate text-left font-medium text-oval-700 hover:underline">
                  {f.file_name}
                </button>
                <span className="pill-grey">
                  {ATTACHMENT_KIND_LABEL[f.kind] ?? f.kind}
                </span>
                {f.size_bytes ? (
                  <span className="hidden text-xs text-slate-400 sm:inline">
                    {(f.size_bytes / 1024).toFixed(0)} KB
                  </span>
                ) : null}
                <span className="hidden text-xs text-slate-400 md:inline">{dateTime(f.uploaded_at)}</span>
                <button type="button" onClick={() => void open(f)} disabled={opening === f.id}
                        className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-oval-700"
                        title="Open in a new tab"
                        aria-label={`Open ${f.file_name}`}>
                  <ExternalLink className="h-4 w-4" />
                </button>
                <button type="button" onClick={() => void save(f)} disabled={saving === f.id}
                        className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-oval-700"
                        title="Save to this computer"
                        aria-label={`Download ${f.file_name}`}>
                  <Download className="h-4 w-4" />
                </button>
              </li>
            )
          })}
        </ul>
      )}
    </Card>
  )
}

// ---------------------------------------------------------------------------
// Files hanging off a list of documents — one query for the whole table rather
// than one per row.
// ---------------------------------------------------------------------------
export function useAttachmentsFor(docType: string, ids: string[]) {
  const [files, setFiles] = useState<Record<string, StoredAttachment[]>>({})
  const key = ids.join(',')

  useEffect(() => {
    let alive = true
    if (!ids.length) { setFiles({}); return }
    void (async () => {
      const { data } = await supabase
        .from('document_attachments')
        .select('id, doc_id, file_name, kind, file_path, mime_type, size_bytes, uploaded_at')
        .eq('doc_type', docType).in('doc_id', ids).order('uploaded_at')
      if (!alive) return
      const grouped: Record<string, StoredAttachment[]> = {}
      for (const f of (data ?? []) as Array<StoredAttachment & { doc_id: string }>) {
        (grouped[f.doc_id] ??= []).push(f)
      }
      setFiles(grouped)
    })()
    return () => { alive = false }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docType, key])

  return files
}

/** Small clickable clips for a table cell. */
// ---------------------------------------------------------------------------
// VENDOR DOCUMENTS
//
// The vendor master keeps the GST certificate, PAN, bank mandate and so on.
// Deliberately a SEPARATE component from AttachmentList rather than a flag on
// it: AttachmentList is on the goods-receipt, invoice and petty-cash screens,
// and those are an audit trail with no notion of replacing anything. Adding a
// "supersede" button behind a prop would have put that button one boolean away
// from a GRN challan.
//
// A vendor's papers go stale - the GST certificate is reissued, the bank
// changes. So a newer file supersedes the old one instead of deleting it: the
// company must be able to show what it held on the day it paid a given invoice.
// Superseded files stay listed, greyed, and can still be opened.
// ---------------------------------------------------------------------------
interface VendorDoc extends StoredAttachment {
  superseded_at: string | null
}

export function VendorDocuments({ vendorId, canEdit, refreshKey }: {
  vendorId: string
  /** Procurement, Accounts and admin. Everyone else never sees this panel. */
  canEdit: boolean
  /** Bump to reload after new files have been queued. */
  refreshKey?: number
}) {
  const [docs, setDocs] = useState<VendorDoc[]>([])
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState<string | null>(null)
  const [showOld, setShowOld] = useState(false)
  const toast = useToast()

  const load = useCallback(async () => {
    setLoading(true)
    const { data, error } = await supabase
      .from('document_attachments')
      .select('id, file_name, kind, file_path, mime_type, size_bytes, uploaded_at, superseded_at')
      .eq('doc_type', 'VENDOR').eq('doc_id', vendorId)
      .order('uploaded_at', { ascending: false })
    // A read failure here is the RLS policy doing its job for somebody who may
    // not see vendor papers. Say nothing rather than showing a red error to a
    // person who is not supposed to know the files exist.
    setDocs(error ? [] : ((data ?? []) as VendorDoc[]))
    setLoading(false)
  }, [vendorId])

  useEffect(() => { void load() }, [load, refreshKey])

  async function open(d: VendorDoc) {
    setBusy(d.id)
    try {
      window.open(await signedViewUrl('doc-attachments', d.file_path), '_blank', 'noopener')
    } catch {
      toast.error('That file could not be opened. It may still be waiting to upload.')
    } finally { setBusy(null) }
  }

  /**
   * Save it, under its own name.
   *
   * Superseded copies download too, and deliberately. When Accounts is asked
   * two years from now which bank account the company held for this vendor on
   * the day it paid a given invoice, the answer is the mandate that was current
   * THEN - which by now is a greyed-out line in the list below.
   */
  async function save(d: VendorDoc) {
    setBusy(d.id)
    try {
      await saveAttachment('doc-attachments', d.file_path, d.file_name)
    } catch {
      toast.error('That file could not be downloaded. It may still be waiting to upload.')
    } finally { setBusy(null) }
  }

  async function supersede(d: VendorDoc) {
    if (!window.confirm(
      `Mark "${d.file_name}" as superseded?\n\nIt stays on file and can still be opened — `
      + `it just stops being the current copy.`)) return
    setBusy(d.id)
    try {
      const { data, error } = await supabase.rpc('rpc_supersede_vendor_document', { p_id: d.id })
      if (error) throw new Error(error.message)
      toast.success(String(data ?? 'Marked as superseded.'))
      await load()
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Could not update that document')
    } finally { setBusy(null) }
  }

  const current = docs.filter((d) => !d.superseded_at)
  const old = docs.filter((d) => d.superseded_at)

  if (loading) return <Loading label="Loading vendor documents…" />

  return (
    <div className="space-y-2">
      {current.length === 0 && (
        <p className="text-sm text-slate-500">
          No documents on file yet. Attach the GST certificate, PAN and bank details above —
          they are optional, and can be added at any time.
        </p>
      )}

      {current.map((d) => (
        <div key={d.id}
             className="flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm">
          <Paperclip className="h-4 w-4 shrink-0 text-slate-400" />
          <span className="pill-grey shrink-0">{ATTACHMENT_KIND_LABEL[d.kind] ?? d.kind}</span>
          <span className="min-w-0 flex-1 truncate">{d.file_name}</span>
          <span className="shrink-0 text-xs text-slate-400">{ddmmyyyy(d.uploaded_at)}</span>
          <button className="btn-ghost btn-sm shrink-0" disabled={busy === d.id}
                  title="Open in a new tab" onClick={() => void open(d)}>
            <ExternalLink className="h-4 w-4" /> Open
          </button>
          <button className="btn-ghost btn-sm shrink-0" disabled={busy === d.id}
                  title="Save to this computer" onClick={() => void save(d)}>
            <Download className="h-4 w-4" /> Download
          </button>
          {canEdit && (
            <button className="btn-ghost btn-sm shrink-0 text-amber-700"
                    disabled={busy === d.id}
                    title="Keep the file but stop treating it as the current copy"
                    onClick={() => void supersede(d)}>
              Supersede
            </button>
          )}
        </div>
      ))}

      {old.length > 0 && (
        <div className="pt-1">
          <button className="btn-ghost btn-sm text-slate-500" onClick={() => setShowOld((v) => !v)}>
            <History className="h-4 w-4" />
            {showOld ? 'Hide' : 'Show'} {old.length} superseded document{old.length === 1 ? '' : 's'}
          </button>
          {showOld && (
            <ul className="mt-1 space-y-1">
              {old.map((d) => (
                <li key={d.id}
                    className="flex items-center gap-2 rounded-lg border border-dashed border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-500">
                  <Paperclip className="h-4 w-4 shrink-0 text-slate-300" />
                  <span className="pill-grey shrink-0">{ATTACHMENT_KIND_LABEL[d.kind] ?? d.kind}</span>
                  <span className="min-w-0 flex-1 truncate line-through">{d.file_name}</span>
                  <span className="shrink-0 text-xs">superseded {ddmmyyyy(d.superseded_at!)}</span>
                  <button className="btn-ghost btn-sm shrink-0" disabled={busy === d.id}
                          title="Open in a new tab" onClick={() => void open(d)}>
                    <ExternalLink className="h-4 w-4" /> Open
                  </button>
                  <button className="btn-ghost btn-sm shrink-0" disabled={busy === d.id}
                          title="Save to this computer" onClick={() => void save(d)}>
                    <Download className="h-4 w-4" /> Download
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// THE VENDOR'S OWN PAPERS, ON THE SCREEN WHERE THE MONEY IS DECIDED
//
// Accounts has always been ALLOWED to open these - fn_may_see_vendor_docs()
// has listed accounts_spoc since 20-09-2026, and the board matrix gives her
// View on the vendor master. What she did not have was a way to reach them
// without leaving the voucher: Master Data -> Vendors -> search -> the eye
// icon, then back again to carry on paying. Four screens to check one bank
// mandate, done several times a day, is a control nobody performs.
//
// So the papers come to her instead. Collapsed by default, because the common
// case is that she does not need them; one click when she does.
//
// This adds NO access. It is the same component, over the same query, under
// the same policy. If the database would not have shown her the file from
// Master Data, it does not show it here either.
// ---------------------------------------------------------------------------

/**
 * Who may open a vendor's papers - the front-end copy of the list.
 *
 * The AUTHORITY is fn_may_see_vendor_docs() in
 * supabase/migrations/20260921000001_vendor_view_access.sql. This exists only
 * to decide whether to DRAW the panel: a role that cannot read the files would
 * otherwise get a card that says "no documents on file", which is a lie about
 * the vendor rather than the truth about the reader.
 *
 * scripts/check-vendor-docs.py reads both and fails if they differ.
 */
export const MAY_SEE_VENDOR_PAPERS = [
  'admin', 'procurement_manager', 'procurement_head', 'accounts_spoc', 'cmd', 'cxo',
] as const

export function maySeeVendorPapers(role: string | null | undefined): boolean {
  return (MAY_SEE_VENDOR_PAPERS as readonly string[]).includes(role ?? '')
}

/**
 * A collapsed card of a vendor's GST certificate, PAN, bank mandate and so on.
 * Renders nothing at all for a role that may not read them.
 */
export function VendorPapersCard({ vendorId, vendorName, subtitle }: {
  vendorId: string | null | undefined
  vendorName?: string | null
  subtitle?: string
}) {
  const { profile } = useAuth()
  const [open, setOpen] = useState(false)

  if (!vendorId || !maySeeVendorPapers(profile?.role)) return null

  return (
    <Card
      title="Vendor papers"
      subtitle={subtitle
        ?? `GST certificate, PAN and bank mandate on file for ${vendorName || vendorId}`}
      actions={
        <button className="btn-secondary btn-sm" onClick={() => setOpen((v) => !v)}>
          <Paperclip className="h-4 w-4" />
          {open ? 'Hide' : 'Show'} papers
        </button>
      }
    >
      {!open ? (
        <p className="text-sm text-slate-500">
          Check the bank mandate before releasing a payment, and the GST certificate before
          claiming the input credit. Both open and download from here — no need to leave this
          voucher.
        </p>
      ) : (
        /* canEdit false everywhere on this side of the house. Adding and
           superseding a vendor's papers is Procurement's job and stays on the
           vendor master; the database refuses it here regardless. */
        <VendorDocuments vendorId={vendorId} canEdit={false} />
      )}
    </Card>
  )
}

export function FileChips({ files, bucket = 'doc-attachments' }: {
  files?: StoredAttachment[]
  bucket?: string
}) {
  const toast = useToast()
  if (!files?.length) return <span className="text-xs text-slate-400">—</span>

  async function open(path: string) {
    try {
      window.open(await signedViewUrl(bucket, path), '_blank', 'noopener')
    } catch { toast.error('That file could not be opened.') }
  }

  /**
   * Ctrl-click (Cmd-click on a Mac) SAVES the clip instead of opening it.
   *
   * A second icon per clip would have made these table cells unreadable - there
   * can be four files against one goods receipt. The modifier is the same one
   * every browser already uses to mean "not in this window", the title says so,
   * and the full Open/Download pair is always there on the document itself.
   */
  async function save(path: string, name: string) {
    try {
      await saveAttachment(bucket, path, name)
    } catch { toast.error('That file could not be downloaded.') }
  }

  return (
    <div className="flex flex-wrap gap-1">
      {files.map((f) => (
        <button key={f.id} type="button"
                onClick={(e) => void (e.ctrlKey || e.metaKey
                  ? save(f.file_path, f.file_name)
                  : open(f.file_path))}
                title={`${f.file_name}\nClick to open · Ctrl-click to download`}
                className="inline-flex max-w-[150px] items-center gap-1 rounded border border-slate-200 px-1.5 py-0.5 text-xs text-oval-700 hover:bg-slate-50">
          <Paperclip className="h-3 w-3 shrink-0" />
          <span className="truncate">{f.file_name}</span>
        </button>
      ))}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Reusable "why can't I do this" explainer
// ---------------------------------------------------------------------------
export function NotPermitted({ what, who }: { what: string; who: string }) {
  return (
    <EmptyState
      title={`You cannot ${what}`}
      message={<>This is handled by the <strong>{who}</strong>. If that is you, sign in with that account.</>}
    />
  )
}
